/**
 * NavShortcuts — one context that powers:
 *   ⭐ Pinned Favorites   — user starred routes
 *   🕒 Recently Used      — auto-tracked navigations
 *   ⌨️ Cmd/Ctrl+K palette — global fuzzy search across all sidebar routes
 *
 * Backed by /api/user-nav on the server. Kept in a React context so the
 * sidebar, top-right shortcut chip, and palette all share the same
 * cache and refresh on the same optimistic updates.
 */
import {
  createContext, useCallback, useContext, useEffect, useMemo,
  useRef, useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";

const API = `${typeof window !== "undefined" ? window.location.origin : ""}/api`;

const NavShortcutsCtx = createContext(null);

/** Flatten a sidebar-config tree into `[{route, label, icon, section}]`. */
export function flattenSidebar(config) {
  const out = [];
  (config?.sections || []).forEach((sec) => {
    if (sec.visible === false) return;
    (sec.items || []).forEach((it) => {
      if (it.visible === false) return;
      if (!it.path) return;
      out.push({
        route:   it.path,
        label:   it.label || it.id,
        icon:    it.icon || sec.icon || "Circle",
        section: sec.title || "",
      });
    });
  });
  return out;
}

export function NavShortcutsProvider({ children, sidebarConfig }) {
  const [pinned,   setPinned]   = useState([]);
  const [recent,   setRecent]   = useState([]);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Debounce so back-to-back navigations (redirects, StrictMode) don't
  // double-count. Also skip the initial mount so a login redirect
  // doesn't create a synthetic entry.
  const lastTrackedRef = useRef({ path: "", ts: 0 });

  const allRoutes = useMemo(() => flattenSidebar(sidebarConfig), [sidebarConfig]);
  const routeMeta = useMemo(() => {
    const m = new Map();
    allRoutes.forEach((r) => m.set(r.route, r));
    return m;
  }, [allRoutes]);

  const loadSummary = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/user-nav/summary?recent_limit=8`);
      setPinned(r.data?.pinned || []);
      setRecent(r.data?.recent || []);
    } catch {
      /* silent — feature degrades gracefully to an empty list */
    }
  }, []);

  useEffect(() => { loadSummary(); }, [loadSummary]);

  // ─── Track navigation ────────────────────────────────────────────
  useEffect(() => {
    const path = location.pathname;
    if (!path || path === "/" || path === "/login") return;
    const meta = routeMeta.get(path);
    if (!meta) return;  // only track known sidebar routes
    const now = Date.now();
    if (lastTrackedRef.current.path === path
        && now - lastTrackedRef.current.ts < 1500) return;
    lastTrackedRef.current = { path, ts: now };
    axios.post(`${API}/user-nav/track`, {
      route:   meta.route,
      label:   meta.label,
      icon:    meta.icon,
      section: meta.section,
    }).then(() => {
      // Optimistic bump — no need to re-fetch every time.
      setRecent((prev) => {
        const filtered = prev.filter((r) => r.route !== meta.route);
        return [{ ...meta, last_used: new Date().toISOString(), count: 1 },
                ...filtered].slice(0, 8);
      });
    }).catch(() => { /* ignore */ });
  }, [location.pathname, routeMeta]);

  // ─── Pin / unpin ────────────────────────────────────────────────
  const pinnedSet = useMemo(
    () => new Set(pinned.map((p) => p.route)), [pinned],
  );

  const togglePin = useCallback(async (route) => {
    const meta = routeMeta.get(route);
    if (!meta) return;
    const isPinned = pinnedSet.has(route);
    // Optimistic update
    if (isPinned) {
      setPinned((prev) => prev.filter((p) => p.route !== route));
    } else {
      setPinned((prev) => [...prev, { ...meta, pinned_at: new Date().toISOString() }]);
    }
    try {
      if (isPinned) {
        await axios.delete(`${API}/user-nav/pin`, { params: { route } });
      } else {
        await axios.post(`${API}/user-nav/pin`, meta);
      }
      toast.success(isPinned ? `Removed ${meta.label} from favorites`
                             : `Pinned ${meta.label} to favorites`);
    } catch (e) {
      toast.error("Failed to update favorites — reverting");
      loadSummary();  // resync from server
    }
  }, [pinnedSet, routeMeta, loadSummary]);

  // ─── Cmd/Ctrl+K global shortcut ─────────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      const isK = e.key === "k" || e.key === "K";
      if (isK && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setPaletteOpen((v) => !v);
      }
      if (e.key === "Escape") setPaletteOpen(false);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  const openPalette  = useCallback(() => setPaletteOpen(true),  []);
  const closePalette = useCallback(() => setPaletteOpen(false), []);
  const gotoRoute    = useCallback((route) => {
    navigate(route);
    setPaletteOpen(false);
  }, [navigate]);

  const value = {
    pinned, recent, allRoutes, pinnedSet,
    togglePin, loadSummary,
    paletteOpen, openPalette, closePalette, gotoRoute,
  };
  return (
    <NavShortcutsCtx.Provider value={value}>
      {children}
    </NavShortcutsCtx.Provider>
  );
}

export function useNavShortcuts() {
  const ctx = useContext(NavShortcutsCtx);
  if (!ctx) {
    // No provider mounted — return an inert shape so components don't crash.
    return {
      pinned: [], recent: [], allRoutes: [], pinnedSet: new Set(),
      togglePin: () => {}, loadSummary: () => {},
      paletteOpen: false, openPalette: () => {}, closePalette: () => {},
      gotoRoute: () => {},
    };
  }
  return ctx;
}
